// frontend/src/components/ambassador/AmbassadorDashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import * as api from '../../api';
import { fmt, fmtK, PayBadge, StatCard, Pager, CollapsibleCard, toDate } from '../common/CommonComponents';
import Leaderboard from '../common/Leaderboard';
import AmbassadorCart from '../common/AmbassadorCart';
import ProductsCatalogView from '../products/ProductsCatalogView';
function AmbassadorDashboard({ showAlert, user, onCartChange }) {
  const [data, setData] = useState(null);
  const [lbData, setLbData] = useState(null);
  const [wdAmount, setWdAmount] = useState("");
  const [wdNotes, setWdNotes] = useState("");
  const [activeTab, setActiveTab] = useState("stats");
  const [cartItems, setCartItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Get unique business identifier
  const businessId = user.businessId || user.business?._id || user.email?.split("@")[0] || "default";
  const cartStorageKey = `ambassador_cart_${businessId}`;

  // Load cart for THIS business only
  useEffect(() => {
    const savedCart = localStorage.getItem(cartStorageKey);
    if (savedCart) {
      try {
        const parsed = JSON.parse(savedCart);
        // Validate cart items belong to this business
        const validItems = parsed.filter(item => item.businessId === businessId);
        setCartItems(validItems);
        if (validItems.length !== parsed.length) {
          // Save cleaned cart
          localStorage.setItem(cartStorageKey, JSON.stringify(validItems));
        }
      } catch(e) {}
    }
  }, [businessId]);

  useEffect(() => {
    if (onCartChange) onCartChange(cartItems.reduce((s, i) => s + i.quantity, 0));
  }, [cartItems, onCartChange]);

  // Save cart for THIS business only
  useEffect(() => {
    localStorage.setItem(cartStorageKey, JSON.stringify(cartItems));
  }, [cartItems, cartStorageKey]);

  const load = () => {
    api.getAmbassadorDashboard().then(setData).catch(() => showAlert("Failed to load", "error"));
    api.getLeaderboard().then(setLbData).catch(() => {});
  };
  
  useEffect(() => { load(); }, []);

  

  const submitWithdrawal = async () => {
    if (!wdAmount) { 
      showAlert("Enter amount", "error"); 
      return; 
    }
    if (Number(wdAmount) > (data?.availableBalance || 0)) {
      showAlert(`Amount exceeds available balance of ${fmt(data.availableBalance)}`, "error");
      return;
    }
    try { 
      await api.submitWithdrawal({ amount: Number(wdAmount), notes: wdNotes }); 
      showAlert("Withdrawal request submitted!", "success"); 
      setWdAmount(""); 
      setWdNotes(""); 
      load(); 
    } catch(e) { 
      showAlert(e.message, "error"); 
    }
  };

  const addToCart = (product, quantity) => {
    setCartItems(prev => {
      const key=product.cartKey||product.productId;
      const existing = prev.find(i => (i.cartKey||i.productId) === key);
      if (existing) {
        return prev.map(i => 
          (i.cartKey||i.productId) === key 
            ? { ...i, quantity: i.quantity + quantity }
            : i
        );
      }
      return [...prev, {
        productId: product.productId,
        name: product.name,
        price: product.sellingPrice,
        quantity: quantity,
        stock: product.quantity,
        businessId: businessId,
        cartKey:key,
        shopId:product.fulfillmentShopId||null,
        shopName:product.fulfillmentShopName||"Central"
      }];
    });
    showAlert(`Added ${quantity} x ${product.name} to cart`, "success");
  };

  const updateCartQuantity = (cartKey, quantity) => {
    setCartItems(prev => 
      prev.map(i => (i.cartKey||i.productId) === cartKey ? { ...i, quantity } : i)
    );
  };

  const removeFromCart = (cartKey) => {
    setCartItems(prev => prev.filter(i => (i.cartKey||i.productId) !== cartKey));
  };

  const submitOrderRequest = async () => {
    if (cartItems.length === 0) {
      showAlert("Cart is empty", "error");
      return;
    }
    
    const total = cartItems.reduce((s, i) => s + (i.price * i.quantity), 0);
    const totalItems = cartItems.reduce((s, i) => s + i.quantity, 0);
    
    if (!window.confirm(`Submit order request for ${totalItems} items totaling ${fmt(total)}?\n\nYour manager will review and process this order.`)) {
      return;
    }
    
    setSubmitting(true);
    try {
      const result = await api.submitAmbassadorOrder({
        items: cartItems.map(i => ({ productId:i.productId,quantity:i.quantity,shopId:i.shopId||null,shopName:i.shopName||"Central" })),
        totalAmount: total,
        notes: `Order request from ambassador ${user.email}`
      });
      
      showAlert(result.message || "Sale request submitted to the responsible manager.", "success");
      setCartItems([]);
      localStorage.removeItem(cartStorageKey);
    } catch(e) {
      showAlert(e.message, "error");
    }
    setSubmitting(false);
  };

  if (!data) return <div className="spinner">Loading your dashboard...</div>;
  
  const currentOrders = lbData ? (lbData.current || []).find(a => a.email === user.email) : null;
  const currentRank = lbData ? (lbData.current || []).findIndex(a => a.email === user.email) : -1;
  const pendingWithdrawals = (data.withdrawals || []).filter(w => w.status === "Pending").length;
  const approvedWithdrawals = (data.withdrawals || []).filter(w => w.status === "Approved").length;

  const getWithdrawalStatusStyle = (status) => {
    switch(status) {
      case "Approved": return { bg: "#10B98120", color: "#10B981", icon: "✅" };
      case "Pending": return { bg: "#F59E0B20", color: "#F59E0B", icon: "⏳" };
      case "Rejected": return { bg: "#EF444420", color: "#EF4444", icon: "❌" };
      default: return { bg: "#94A3B820", color: "#94A3B8", icon: "📋" };
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <div style={{ 
        background: "linear-gradient(135deg,#0B1120 0%,#1E293B 100%)", 
        borderRadius: 16, 
        padding: 24, 
        color: "#fff", 
        marginBottom: 20,
        position: "relative",
        overflow: "hidden"
      }}>
        <div style={{ position: "absolute", top: -40, right: -40, width: 150, height: 150, background: "radial-gradient(circle,rgba(16,185,129,.2),transparent 70%)", pointerEvents: "none" }} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
          <div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,.5)", marginBottom: 4 }}>Welcome back,</div>
            <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 4 }}>{user.name || user.email?.split("@")[0]}</div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,.4)" }}>
              Commission Rate: <strong style={{ color: "#10B981" }}>{data.commissionRate}%</strong>
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,.5)", marginBottom: 4 }}>Available Balance</div>
            <div style={{ fontSize: 28, fontWeight: 900, color: "#10B981", letterSpacing: "-1px" }}>{fmt(data.availableBalance)}</div>
            {pendingWithdrawals > 0 && (
              <div style={{ fontSize: 10, color: "rgba(255,255,255,.3)", marginTop: 4 }}>
                {pendingWithdrawals} pending withdrawal request(s)
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="tab-switch">
        <button className={activeTab === "stats" ? "active" : ""} onClick={() => setActiveTab("stats")}>
          📊 My Stats
        </button>
        <button className={activeTab === "leaderboard" ? "active" : ""} onClick={() => setActiveTab("leaderboard")}>
          🏆 Leaderboard
        </button>
        <button className={activeTab === "products" ? "active" : ""} onClick={() => setActiveTab("products")}>
          🛍️ Shop & Order
          {cartItems.length > 0 && <span className="nav-badge" style={{ marginLeft: 4 }}>{cartItems.length}</span>}
        </button>
      </div>

      {/* Stats Tab */}
      {activeTab === "stats" && (
        <>
          <div className="card">
            <h2>🏅 All-Time Performance</h2>
            <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 12 }}>
              Commission Rate: <strong>{data.commissionRate}%</strong> of realized profit
            </p>
            <div className="stats-grid">
              <StatCard label="Total Activities (All Time)" value={data.totalActivities || data.totalOrders} sub="Custom orders + walk-in referrals" />
              <StatCard label="Total Sales (All Time)" value={fmt(data.totalSales)} />
              <StatCard label="All-Time Earnings" value={fmt(data.totalEarned)} cls="success" sub="From paid orders only" />
              <StatCard label="Available to Withdraw" value={fmt(data.availableBalance)} cls="info" />
            </div>
          </div>

          <div className="card">
            <h2>📅 {lbData?.currentPeriod || "Current Period"}</h2>
            {lbData?.lastResetDate && (
              <p style={{ fontSize: 11, color: "#9ca3af", marginBottom: 10 }}>
                Period started: {new Date(lbData.lastResetDate).toLocaleDateString("en-NG", { day: "numeric", month: "long", year: "numeric" })}
              </p>
            )}
            <div className="stats-grid">
              <StatCard label="Orders This Period" value={currentOrders?.orderCount || 0} />
              <StatCard label="Sales This Period" value={fmt(currentOrders?.totalSales || 0)} />
              <StatCard label="Earned This Period" value={fmt(currentOrders?.totalEarned || 0)} cls="success" />
              <StatCard label="Leaderboard Rank" value={(() => {
                if (!lbData?.current?.length) return "—";
                const idx = lbData.current.findIndex(a => a.email === user.email);
                return idx >= 0 ? `#${idx + 1}` : "—";
              })()} cls="warning" />
            </div>
            <div className="preview-box" style={{ fontSize: 11 }}>
              💡 Earnings are on <strong>paid orders only</strong>. Partial payments contribute proportionally. Unpaid orders show ₦0.00 until payment is received.
            </div>
          </div>

          <div className="card">
            <h2>📦 Activity Breakdown</h2>
            <div className="stats-grid-3">
              <StatCard label="Paid (incl. Walk-in)" value={data.paid} cls="success" sub="Custom paid + all walk-in sales" />
              <StatCard label="Partial (Custom Orders)" value={data.partial} cls="warning" />
              <StatCard label="Unpaid (Custom Orders)" value={data.unpaid} cls="accent" />
            </div>
            {(data.walkInSalesCount || 0) > 0 && (
              <div className="preview-box" style={{ fontSize: 11, marginTop: 8 }}>
                📊 Includes <strong>{data.walkInSalesCount}</strong> walk-in sale referral(s) totalling <strong>{fmt(data.walkInSalesAmount)}</strong> — all confirmed paid.
              </div>
            )}
          </div>

          <div className="card">
            <h2>💸 Request Payout</h2>
            <div className="alert alert-info" style={{ marginBottom: 12 }}>
              Available to withdraw: <strong>{fmt(data.availableBalance)}</strong>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Amount (₦)</label>
                <input 
                  type="number" 
                  value={wdAmount} 
                  onChange={e => setWdAmount(e.target.value)} 
                  placeholder="Amount"
                  max={data.availableBalance}
                />
              </div>
              <div className="form-group">
                <label>Notes</label>
                <input value={wdNotes} onChange={e => setWdNotes(e.target.value)} placeholder="Optional" />
              </div>
            </div>
            <button 
              className="btn btn-primary" 
              onClick={submitWithdrawal} 
              disabled={!wdAmount || Number(wdAmount) > data.availableBalance}
            >
              Submit Withdrawal Request
            </button>
            <p style={{ fontSize: 11, color: "#9ca3af", marginTop: 8 }}>
              Your manager will review and process your request.
            </p>
          </div>

          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--border)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h2 style={{ margin: 0 }}>📋 Withdrawal History</h2>
                <div style={{ display: "flex", gap: 8 }}>
                  {pendingWithdrawals > 0 && (
                    <span style={{ fontSize: 10, background: "#F59E0B20", color: "#F59E0B", padding: "2px 8px", borderRadius: 4 }}>
                      {pendingWithdrawals} pending
                    </span>
                  )}
                  {approvedWithdrawals > 0 && (
                    <span style={{ fontSize: 10, background: "#10B98120", color: "#10B981", padding: "2px 8px", borderRadius: 4 }}>
                      {approvedWithdrawals} approved
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="table-wrap">
              <table className="w-full">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Notes</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.withdrawals || []).length === 0 && (
                    <tr><td colSpan={5} style={{ textAlign: "center", color: "#9ca3af", padding: 24 }}>No withdrawal requests yet.</td></tr>
                  )}
                  {(data.withdrawals || []).map(w => {
                    const statusStyle = getWithdrawalStatusStyle(w.status);
                    return (
                      <tr key={w.withdrawalId}>
                        <td style={{ fontFamily: "monospace", fontSize: 11 }}>{w.withdrawalId}</td>
                        <td style={{ fontWeight: 700, color: "#10B981" }}>{fmt(w.amount)}</td>
                        <td>
                          <span style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            padding: "2px 8px",
                            borderRadius: 4,
                            fontSize: 10,
                            fontWeight: 700,
                            background: statusStyle.bg,
                            color: statusStyle.color
                          }}>
                            {statusStyle.icon} {w.status}
                          </span>
                        </td>
                        <td style={{ fontSize: 11, color: "var(--text-3)" }}>{w.notes || "—"}</td>
                        <td style={{ fontSize: 11 }}>{new Date(w.createdAt).toLocaleDateString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--border)" }}>
              <h2 style={{ margin: 0 }}>📦 My Orders</h2>
            </div>
            <div className="table-wrap">
              <table className="w-full">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Item</th>
                    <th>Sales</th>
                    <th>Paid</th>
                    <th>My Earning</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.orders || []).length === 0 && (
                    <tr><td colSpan={6} style={{ textAlign: "center", color: "#9ca3af", padding: 24 }}>No orders yet.</td></tr>
                  )}
                  {(data.orders || []).map(o => (
                    <tr key={o.orderId}>
                      <td style={{ fontFamily: "monospace", fontSize: 11 }}>{o.orderId}</td>
                      <td>{o.item}</td>
                      <td>{fmt(o.totalSales)}</td>
                      <td>{fmt(o.totalPaid)}</td>
                      <td style={{ color: "#10B981", fontWeight: 600 }}>{fmt(o.ambassadorEarning)}</td>
                      <td><PayBadge status={o.paymentStatus} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Leaderboard Tab */}
      {activeTab === "leaderboard" && (
        <div className="card">
          <h2>🏆 Ambassador Leaderboard</h2>
          <Leaderboard data={lbData} currentUserEmail={user.email} showReset={false} loading={!lbData} />
        </div>
      )}

      {/* Products Tab with Cart */}
      {activeTab === "products" && (
        <div>
          <ProductsCatalogView 
            showAlert={showAlert} 
            user={user} 
            onAddToCart={addToCart}
            cartItems={cartItems}
          />
          <AmbassadorCart 
            id="cart-section"
            cartItems={cartItems}
            onUpdateQuantity={updateCartQuantity}
            onRemoveItem={removeFromCart}
            onSubmit={submitOrderRequest}
            submitting={submitting}
          />
        </div>
      )}
    </div>
  );
}

export default AmbassadorDashboard;